import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, MapPin, Phone, Mail, BadgeCheck, Clock, Zap, Shield, Sun, Loader2, ChevronRight, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import GlassCard from "@/components/GlassCard";
import AddProviderDialog from "@/components/AddProviderDialog";
import { useProviders } from "@/hooks/useProviders";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useMemo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Search, Filter } from "lucide-react";
import { SuburbSelect } from "@/components/SuburbSelect";
import { motion, AnimatePresence } from "framer-motion";
import ContactOptionsDialog from "@/components/ContactOptionsDialog";
import ProviderDetailsSheet from "@/components/ProviderDetailsSheet";
import { cn } from "@/lib/utils";

const ServiceProviders = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { taskTitle, category } = location.state || {};
    const { user } = useAuth();

    const { providers, loading, refetch } = useProviders(category);

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSuburb, setSelectedSuburb] = useState("");
    const [contactDialog, setContactDialog] = useState({ open: false, provider: null, type: 'phone' });
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [detailsSheet, setDetailsSheet] = useState({ open: false, provider: null });

    // Memoize filtered providers to prevent flashing
    const filteredProviders = useMemo(() => {
        return providers.filter((provider) => {
            // Exclude rejected providers from everyone EXCEPT the person who
            // submitted them — the admin's reject action explicitly tells the
            // admin "hidden from public directory but still visible to the
            // submitter," so a rejected provider should stay visible in the
            // submitter's own "Your Activity" list.
            if (provider.is_rejected && provider.added_by_user_id !== user?.id) return false;
            const matchesSearch = !searchQuery || provider.name.toLowerCase().includes(searchQuery.toLowerCase());

            // Smart Suburb Match: Check display text AND the new service_suburbs array
            const matchesSuburb = !selectedSuburb ||
                provider.service_area?.toLowerCase().includes(selectedSuburb.toLowerCase()) ||
                (provider.service_suburbs && provider.service_suburbs.some(s => s.toLowerCase() === selectedSuburb.toLowerCase()));

            return matchesSearch && matchesSuburb;
        });
    }, [providers, searchQuery, selectedSuburb, user?.id]);


    // Separate providers by usage, featured, neighbourhood, and others
    const historyProviders = filteredProviders
        .filter(p => p.lastUsedDate || p.isMine)
        .sort((a, b) => {
            if (a.lastUsedDate && b.lastUsedDate) return new Date(b.lastUsedDate) - new Date(a.lastUsedDate);
            if (a.lastUsedDate) return -1;
            if (b.lastUsedDate) return 1;
            return new Date(b.created_at) - new Date(a.created_at);
        });

    const featuredProviders = filteredProviders.filter(p => p.is_featured && !historyProviders.some(h => h.id === p.id));

    // Community Favorites: Original Logic Kept Exactly
    const neighbourhoodFavorites = filteredProviders
        .filter(p => p.isNeighbourhoodFavorite && !p.is_featured)
        .sort((a, b) => (b.communityUsageCount || 0) - (a.communityUsageCount || 0));

    // Professional Directory: Main category list
    const otherProviders = filteredProviders.filter(
        p => !p.is_featured && !historyProviders.some(h => h.id === p.id) && !neighbourhoodFavorites.some(n => n.id === p.id)
    ).sort((a, b) => a.name.localeCompare(b.name));

    const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
    const [hasAutoExpanded, setHasAutoExpanded] = useState(false);

    // Auto-expand "Your Activity" if there are no verified providers
    useEffect(() => {
        if (!loading && providers.length > 0 && !hasAutoExpanded) {
            const hasVerified = featuredProviders.length > 0 || neighbourhoodFavorites.length > 0 || otherProviders.length > 0;
            if (!hasVerified && historyProviders.length > 0) {
                setIsHistoryExpanded(true);
            }
            setHasAutoExpanded(true);
        }
    }, [loading, providers.length, featuredProviders.length, neighbourhoodFavorites.length, otherProviders.length, historyProviders.length, hasAutoExpanded]);

    const [showAllCommunity, setShowAllCommunity] = useState(false);
    const [showAllOthers, setShowAllOthers] = useState(false);

    const displayedHistory = historyProviders.slice(0, 3);
    const displayedCommunityFavorites = showAllCommunity ? neighbourhoodFavorites : neighbourhoodFavorites.slice(0, 3);
    const displayedOthers = showAllOthers ? otherProviders : otherProviders.slice(0, 3);

    const getSpecialtyIcon = (specialty) => {
        if (specialty.includes("Solar")) return Sun;
        if (specialty.includes("24/7") || specialty.includes("Emergency")) return Zap;
        return Shield;
    };

    const handleOpenContact = (provider, type) => {
        setContactDialog({ open: true, provider, type });
    };

    const handleOpenDetails = (provider) => {
        setDetailsSheet({ open: true, provider });
    };

    const ProviderCard = ({ provider, featured = false }) => (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            transition={{ duration: 0.3 }}
            onClick={() => handleOpenDetails(provider)}
        >
            <GlassCard className={`p-4 ${featured ? "border-primary/30 ring-1 ring-primary/10" : "border-none"} bg-white/40 hover:bg-white/60 transition-colors shadow-sm hover:shadow-md rounded-2xl`}>
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2">
                            <h3 className="font-bold text-foreground truncate">{provider.name}</h3>
                            {provider.is_verified && (
                                <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
                            )}
                            {provider.is_rejected && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive shrink-0">
                                    Rejected
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground font-medium flex-wrap">
                            <span className="flex items-center gap-1 shrink-0">
                                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                                <span className="text-foreground">{provider.rating || "N/A"}</span>
                                {provider.review_count && provider.review_count > 0 && (
                                    <span className="text-muted-foreground">({provider.review_count})</span>
                                )}
                            </span>
                            {provider.service_area && (
                                <span className="flex items-center gap-1 truncate">
                                    <MapPin className="w-3.5 h-3.5 text-primary/40 shrink-0" />
                                    <span className="truncate">{provider.service_area}</span>
                                    {provider.service_suburbs?.length > 1 && (
                                        <span className="ml-1 bg-primary/5 text-primary text-[8px] px-1.5 py-0.5 rounded-md font-bold uppercase tracking-tighter">
                                            Wide Area
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <Button
                            size="sm"
                            variant="outline"
                            className="bg-white/50 border-none shadow-sm hover:bg-primary/10 shrink-0 h-9 w-9 p-0 rounded-xl"
                            onClick={(e) => { e.stopPropagation(); handleOpenContact(provider, 'email'); }}
                        >
                            <Mail className="w-4 h-4 text-primary" />
                        </Button>
                        <Button
                            size="sm"
                            variant={provider.lastUsedDate ? "default" : "outline"}
                            className="shrink-0 rounded-xl h-9 w-9 p-0 bg-primary text-primary-foreground hover:bg-primary/90 border-none shadow-sm"
                            onClick={(e) => { e.stopPropagation(); handleOpenContact(provider, 'phone'); }}
                        >
                            <Phone className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </GlassCard>
        </motion.div>
    );

    const FeaturedProviderCard = ({ provider }) => (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
        >
            <GlassCard className="p-6 border-none bg-gradient-to-br from-white/80 to-white/40 shadow-xl shadow-primary/5 rounded-[2rem] relative overflow-hidden group">
                {/* Decorative background element */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl transition-colors group-hover:bg-primary/10" />

                <div className="relative space-y-5">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <h3 className="font-extrabold text-foreground text-xl tracking-tight">{provider.name}</h3>
                                <div className="bg-primary/10 p-1 rounded-full">
                                    <BadgeCheck className="w-5 h-5 text-primary shrink-0" />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 bg-amber-500/10 px-2 py-0.5 rounded-lg">
                                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                    <span className="font-bold text-amber-700 text-sm">{provider.rating || "N/A"}</span>
                                </div>
                                {provider.review_count && provider.review_count > 0 && (
                                    <span className="text-xs text-muted-foreground font-medium">
                                        from {provider.review_count} community reviews
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Service Area */}
                    {provider.service_area && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground font-medium bg-gray-50/50 p-2 rounded-xl">
                            <MapPin className="w-4 h-4 text-primary/60 shrink-0" />
                            <span>Servicing {provider.service_area}</span>
                        </div>
                    )}

                    {/* Specialty Tags */}
                    {provider.specialties && provider.specialties.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {provider.specialties.map((specialty, idx) => {
                                const Icon = getSpecialtyIcon(specialty);
                                return (
                                    <span
                                        key={idx}
                                        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-white shadow-sm text-primary px-3 py-1.5 rounded-full border border-primary/5"
                                    >
                                        <Icon className="w-3 h-3" />
                                        {specialty}
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {/* Quick Quote Button */}
                    <div className="pt-2 flex gap-3">
                        <Button
                            className="flex-1 rounded-2xl text-base py-6 font-bold shadow-lg shadow-gray-200 hover:shadow-gray-300 transition-all border border-gray-100 bg-white text-foreground hover:bg-gray-50"
                            onClick={() => handleOpenContact(provider, 'email')}
                        >
                            <Mail className="w-4 h-4 mr-2 text-primary" />
                            Email
                        </Button>
                        <Button
                            className="flex-1 rounded-2xl text-base py-6 font-bold shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all border-none"
                            onClick={() => handleOpenContact(provider, 'phone')}
                        >
                            <Phone className="w-4 h-4 mr-2" />
                            Call
                        </Button>
                    </div>
                </div>
            </GlassCard>
        </motion.div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-24">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-40 bg-white/60 backdrop-blur-2xl border-b border-gray-100" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(-1)}
                        className="rounded-xl hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex flex-col">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/40 leading-none mb-1">
                            Service Directory
                        </span>
                        <h1 className="text-xl font-extrabold text-foreground tracking-tight">
                            {taskTitle || "Care Professionals"}
                        </h1>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 6rem)" }}>
                <div className="max-w-lg mx-auto space-y-8">
                    {/* Search and Filter */}
                    <div className="flex gap-2">
                        <div className="relative flex-[1.4]">
                            <Input
                                placeholder="Search Name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pr-10 rounded-2xl h-12 bg-white/50 border-none shadow-sm focus-visible:ring-primary/20"
                            />
                            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        </div>
                        <SuburbSelect
                            value={selectedSuburb}
                            onSelect={(val) => setSelectedSuburb(val)}
                            placeholder="Suburb"
                            className="flex-1 h-12 rounded-2xl bg-white/50 border-none shadow-sm"
                        />
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    ) : (filteredProviders.length === 0 && providers.length > 0) ? (
                        <div className="py-12 text-center space-y-3">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto opacity-50">
                                <Search className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <p className="text-muted-foreground font-medium">No providers match your search.</p>
                        </div>
                    ) : providers.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center"
                        >
                            <GlassCard className="p-8 border border-primary/20 bg-white/50 backdrop-blur-xl rounded-[2.5rem] shadow-xl shadow-primary/5 space-y-5">
                                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
                                    <UserPlus className="w-8 h-8 text-primary" />
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-lg font-bold text-foreground">Be the first to add a provider!</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
                                        Help us grow the HomeEzi directory in this category. Add your favorite trusted local professional to get started.
                                    </p>
                                </div>
                                <div className="pt-2">
                                    <Button
                                        onClick={() => setAddDialogOpen(true)}
                                        className="rounded-xl px-6 py-5 font-bold shadow-lg shadow-primary/20 border-none"
                                    >
                                        Add a Provider
                                    </Button>
                                </div>
                            </GlassCard>
                        </motion.div>
                    ) : (
                        <div className="space-y-10">
                            {/* Your Activity (Collapsed by default) */}
                            {historyProviders.length > 0 && (
                                <section className="space-y-3">
                                    <button
                                        onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                                        className="w-full flex items-center justify-between px-1 group"
                                    >
                                        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] group-hover:text-primary transition-colors">
                                            Your Activity
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-bold text-primary/40 group-hover:text-primary transition-colors">
                                                {isHistoryExpanded ? "Hide" : "Show Recent"}
                                            </span>
                                            <ChevronRight className={cn(
                                                "w-3 h-3 text-primary/40 transition-transform duration-200",
                                                isHistoryExpanded ? "rotate-90" : "rotate-0"
                                            )} />
                                        </div>
                                    </button>
                                    <AnimatePresence>
                                        {isHistoryExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="overflow-hidden space-y-3"
                                            >
                                                {displayedHistory.map((provider) => (
                                                    <ProviderCard key={provider.id} provider={provider} featured={historyProviders[0].id === provider.id} />
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </section>
                            )}

                            {/* Curated Specialists */}
                            {featuredProviders.length > 0 && (
                                <section className="space-y-4">
                                    <div className="flex items-center justify-between ml-1">
                                        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">
                                            Curated Specialists
                                        </h3>
                                    </div>
                                    <div className="space-y-4">
                                        {featuredProviders.map((provider) => (
                                            <FeaturedProviderCard key={provider.id} provider={provider} />
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Community Favorites */}
                            {neighbourhoodFavorites.length > 0 && (
                                <section className="space-y-4">
                                    <div className="flex items-center justify-between ml-1">
                                        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">
                                            Community Favorites
                                        </h3>
                                        <span className="text-[10px] bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded-full font-bold">
                                            Trusted
                                        </span>
                                    </div>
                                    <div className="space-y-3">
                                        {displayedCommunityFavorites.map((provider) => (
                                            <div key={provider.id} className="relative">
                                                <ProviderCard provider={provider} />
                                                <div className="absolute top-2.5 right-14 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                                                    {provider.communityUsageCount} {provider.communityUsageCount === 1 ? 'home' : 'homes'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {neighbourhoodFavorites.length > 3 && (
                                        <Button
                                            variant="ghost"
                                            onClick={() => setShowAllCommunity(!showAllCommunity)}
                                            className="w-full text-xs font-bold text-primary hover:bg-primary/5 uppercase tracking-wider"
                                        >
                                            {showAllCommunity ? 'Show Less' : `Show ${neighbourhoodFavorites.length - 3} More Favorites`}
                                        </Button>
                                    )}
                                </section>
                            )}

                            {/* Nearby Professionals */}
                            {otherProviders.length > 0 && (
                                <section className="space-y-4">
                                    <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] ml-1">
                                        Nearby Pros
                                    </h3>
                                    <div className="space-y-3">
                                        {displayedOthers.map((provider) => (
                                            <ProviderCard key={provider.id} provider={provider} />
                                        ))}
                                    </div>
                                    {otherProviders.length > 3 && (
                                        <Button
                                            variant="ghost"
                                            onClick={() => setShowAllOthers(!showAllOthers)}
                                            className="w-full text-xs font-bold text-primary hover:bg-primary/5 uppercase tracking-wider"
                                        >
                                            {showAllOthers ? 'Show Less' : `Show ${otherProviders.length - 3} More Professionals`}
                                        </Button>
                                    )}
                                </section>
                            )}

                            {/* Empty Verified State Notice */}
                            {featuredProviders.length === 0 && neighbourhoodFavorites.length === 0 && otherProviders.length === 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <GlassCard className="p-6 border border-primary/15 bg-white/40 backdrop-blur-xl rounded-[2rem] shadow-md shadow-primary/5 space-y-4">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                                                <Shield className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="space-y-1 flex-1">
                                                <h4 className="font-extrabold text-foreground text-base tracking-tight">No Verified Providers Yet</h4>
                                                <p className="text-xs text-muted-foreground leading-relaxed">
                                                    There are no verified community providers listed in this category yet.
                                                    {historyProviders.length > 0 ? (
                                                        <span> Check <strong>Your Activity</strong> above to find your added providers, or add another one below.</span>
                                                    ) : (
                                                        <span> Add a trusted local provider to get started.</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-end pt-1">
                                            <Button
                                                onClick={() => setAddDialogOpen(true)}
                                                size="sm"
                                                className="rounded-xl font-bold bg-primary text-primary-foreground hover:bg-primary/90 border-none shadow-sm h-9 px-4"
                                            >
                                                Add a Provider
                                            </Button>
                                        </div>
                                    </GlassCard>
                                </motion.div>
                            )}
                        </div>
                    )}
                </div>
            </main>

            {/* Contact Dialog */}
            <ContactOptionsDialog
                open={contactDialog.open}
                onOpenChange={(open) => setContactDialog(prev => ({ ...prev, open }))}
                provider={contactDialog.provider}
                type={contactDialog.type}
            />

            {/* Add Provider Dialog */}
            <AddProviderDialog
                open={addDialogOpen}
                onOpenChange={setAddDialogOpen}
                onSuccess={() => refetch()}
            />

            {/* Provider Details Sheet */}
            <ProviderDetailsSheet
                open={detailsSheet.open}
                onOpenChange={(open) => setDetailsSheet(prev => ({ ...prev, open }))}
                provider={detailsSheet.provider}
                onUpdated={() => refetch()}
            />
        </div>
    );
};

export default ServiceProviders;
